import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, Check, CheckCheck, Trash2, AlertCircle, ArrowRightCircle, UserPlus, Clock, CreditCard, FileBarChart } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";

const ICON_BY_TIPO = {
  sla_amarelo: Clock,
  sla_vermelho: AlertCircle,
  mudanca_etapa: ArrowRightCircle,
  atribuicao: UserPlus,
  fatura_vencendo: CreditCard,
  relatorio_semanal: FileBarChart,
};

const COLOR_BY_URGENCIA = {
  alta: "text-red-600 bg-red-50",
  media: "text-amber-600 bg-amber-50",
  info: "text-blue-600 bg-blue-50",
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s atras`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min atras`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atras`;
  return `${Math.floor(diff / 86400)}d atras`;
}

export default function NotificationBell({ testIdSuffix = "" }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [knownIds, setKnownIds] = useState(new Set());
  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };

  const playSound = (urgencia) => {
    try {
      // Tons sintetizados via WebAudio (sem dependencias / sem APIs externas)
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = urgencia === "alta" ? 880 : 660;
      osc.type = "sine";
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      osc.start();
      osc.stop(ctx.currentTime + 0.4);
      if (urgencia === "alta") {
        // Segundo bip para urgentes
        setTimeout(() => {
          const o2 = ctx.createOscillator();
          const g2 = ctx.createGain();
          o2.connect(g2); g2.connect(ctx.destination);
          o2.frequency.value = 1100;
          g2.gain.setValueAtTime(0.0001, ctx.currentTime);
          g2.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
          g2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
          o2.start();
          o2.stop(ctx.currentTime + 0.35);
        }, 250);
      }
    } catch { /* sound failed - silent */ }
  };

  const showBrowserNotif = (n) => {
    try {
      if (typeof window === "undefined" || !("Notification" in window)) return;
      if (Notification.permission !== "granted") return;
      const notif = new Notification(n.titulo || "Notificacao CRM", {
        body: n.mensagem || "",
        icon: "/brand/obrapadrao-logo.jpeg",
        tag: `crm-${n.id}`,
        requireInteraction: n.urgencia === "alta",
      });
      notif.onclick = () => { window.focus(); notif.close(); };
    } catch { /* silent */ }
  };

  const fetchAll = useCallback(async (isFirstLoad = false) => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/crm/notifications`, { headers });
      const novos = res.data.items || [];
      setUnread(res.data.total_nao_lidas || 0);
      // Detectar novas notificacoes (nao estavam em knownIds)
      if (!isFirstLoad) {
        const novasUnread = novos.filter(n => !n.lida && !knownIds.has(n.id));
        if (novasUnread.length > 0) {
          const maisUrgente = novasUnread.reduce((a, b) => (b.urgencia === "alta" ? b : a), novasUnread[0]);
          playSound(maisUrgente.urgencia);
          novasUnread.slice(0, 3).forEach(showBrowserNotif);
        }
      }
      setItems(novos);
      setKnownIds(new Set(novos.map(n => n.id)));
    } catch {
      // silent fail
    }
  }, [token, knownIds]);

  useEffect(() => {
    fetchAll(true); // primeira carga nao toca som
    const interval = setInterval(() => fetchAll(false), 60000); // refresh a cada 60s
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/crm/notifications/${id}/marcar-lida`, {}, { headers });
      fetchAll();
    } catch { toast.error("Erro"); }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/crm/notifications/marcar-todas-lidas`, {}, { headers });
      toast.success("Todas marcadas como lidas");
      fetchAll();
    } catch { toast.error("Erro"); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/crm/notifications/${id}`, { headers });
      fetchAll();
    } catch { toast.error("Erro"); }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid={`notification-bell-btn${testIdSuffix ? "-" + testIdSuffix : ""}`}>
          <Bell className="h-5 w-5 text-slate-600" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-unread-count">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0 max-h-[480px] overflow-hidden flex flex-col" data-testid="notification-dropdown">
        <div className="flex items-center justify-between px-4 py-3 border-b sticky top-0 bg-white">
          <div>
            <p className="text-sm font-semibold text-[#0A1628]">Notificações</p>
            <p className="text-xs text-slate-500">{unread} não lida{unread === 1 ? "" : "s"}</p>
          </div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs h-7" data-testid="btn-marcar-todas-lidas">
              <CheckCheck className="h-3.5 w-3.5 mr-1" /> Marcar todas
            </Button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
              Nenhuma notificacao
            </div>
          ) : (
            items.map((n) => {
              const Icon = ICON_BY_TIPO[n.tipo] || Bell;
              const colorClasses = COLOR_BY_URGENCIA[n.urgencia] || "text-slate-600 bg-slate-50";
              return (
                <div
                  key={n.id}
                  className={`px-4 py-3 border-b hover:bg-slate-50 transition-colors ${!n.lida ? "bg-blue-50/40" : ""}`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${colorClasses}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0A1628] truncate">{n.titulo}</p>
                      <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">{n.mensagem}</p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[10px] text-slate-400">{timeAgo(n.created_at)}</span>
                        <div className="flex items-center gap-1">
                          {n.lead_id && (
                            <Link to="/comercial/crm" onClick={() => { setOpen(false); markRead(n.id); }}>
                              <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2">Ver lead</Button>
                            </Link>
                          )}
                          {n.tipo === "fatura_vencendo" && (
                            <Link to="/financeiro/cartoes" onClick={() => { setOpen(false); markRead(n.id); }}>
                              <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" data-testid={`btn-ver-fatura-${n.id}`}>Ver fatura</Button>
                            </Link>
                          )}
                          {n.tipo === "relatorio_semanal" && (
                            <Link to="/financeiro/fluxo-caixa" onClick={() => { setOpen(false); markRead(n.id); }}>
                              <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" data-testid={`btn-ver-fluxo-${n.id}`}>Ver fluxo</Button>
                            </Link>
                          )}
                          {!n.lida && (
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => markRead(n.id)} title="Marcar como lida">
                              <Check className="h-3 w-3 text-emerald-600" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => remove(n.id)} title="Excluir">
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
